import {
  IS_PROD,
  SOCIAL_PIXEL_ID,
  SOCIAL_TEST_EVENT_CODE,
} from '@/lib/constant'
import type { ConversionEventData, ConversionEventType } from './types'
import { CONVERSION_EVENT_TYPES } from './types'
import { hashEmail } from './utils'

/**
 * Social Conversions API
 */
export async function sendToSocialPixel(
  eventData: ConversionEventData,
): Promise<void> {
  // 在非生产环境且没有测试码时，仅打印日志
  if (!IS_PROD && !SOCIAL_TEST_EVENT_CODE) {
    console.log('Social Conversions API Event (dev, no test code):', eventData)
    return
  }

  try {
    const apiKey = process.env.SOCIAL_CONVERSION_API_KEY
    const pixelId = SOCIAL_PIXEL_ID

    if (!apiKey || !pixelId) {
      console.warn('Social Conversions API credentials not configured')
      return
    }

    // 映射事件类型到Social标准事件
    const fbEventMap: Record<ConversionEventType, string> = {
      [CONVERSION_EVENT_TYPES.SIGNUP]: 'CompleteRegistration',
      [CONVERSION_EVENT_TYPES.REFERRAL_SIGNUP]: 'CompleteRegistration',
      [CONVERSION_EVENT_TYPES.LOGIN]: 'Login',
      [CONVERSION_EVENT_TYPES.START_CODING]: 'StartTrial',
      [CONVERSION_EVENT_TYPES.FINISH_CODING]: 'Lead',
      [CONVERSION_EVENT_TYPES.CHECKOUT]: 'InitiateCheckout',
      [CONVERSION_EVENT_TYPES.ORDER_SUCCESS]: 'Purchase',
      [CONVERSION_EVENT_TYPES.SUBSCRIBE_SUCCESS]: 'Subscribe',
    }

    const fbEventName = fbEventMap[eventData.event_type] || 'CustomEvent'

    // 构建用户数据
    const userData: any = {
      external_id: eventData.user_id,
    }

    // 添加邮箱哈希（如果提供）
    if (eventData.user_email) {
      const hashedEmail = await hashEmail(eventData.user_email)
      if (hashedEmail) {
        userData.em = [hashedEmail]
      }
    }

    // 添加Social特有的匹配信号
    if (eventData.social_fbp) {
      userData.fbp = eventData.social_fbp
    }
    if (eventData.social_fbc) {
      userData.fbc = eventData.social_fbc
    }

    // 构建自定义数据
    const customData: any = {}
    if (eventData.value) {
      customData.value = eventData.value
    }
    if (eventData.currency) {
      customData.currency = eventData.currency.toLowerCase()
    }
    if (eventData.transaction_id) {
      customData.content_ids = [eventData.transaction_id]
    }

    // 构建事件数据
    const eventPayload = {
      event_name: fbEventName,
      event_time: Math.floor(Date.now() / 1000),
      user_data: userData,
      custom_data: customData,
      action_source: 'website',
    }

    // 使用FormData格式发送请求
    const formData = new FormData()
    formData.append('data', JSON.stringify([eventPayload]))
    formData.append('access_token', apiKey)

    // 如果是测试模式，添加 test_event_code
    if (!IS_PROD && SOCIAL_TEST_EVENT_CODE) {
      formData.append('test_event_code', SOCIAL_TEST_EVENT_CODE)
      console.log(
        `Social CAPI Test Event Sent: ${fbEventName} with code ${SOCIAL_TEST_EVENT_CODE}`,
      )
    }

    const response = await fetch(
      `https://graph.social.com/v23.0/${pixelId}/events`,
      {
        method: 'POST',
        body: formData,
      },
    )

    if (!response.ok) {
      const errorText = await response.text()
      console.error(
        'Failed to send event to Social Conversions API:',
        response.status,
        response.statusText,
        errorText,
      )
    } else {
      if (IS_PROD) {
        console.log(
          'Successfully sent event to Social Conversions API:',
          fbEventName,
        )
      }
    }
  } catch (error) {
    console.error('Error sending event to Social Conversions API:', error)
  }
}
